import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/_layout/privacy")({
  component: PrivacyPolicy,
  head: () => ({
    meta: [
      {
        property: "og:title",
        content: "Privacy Policy - Macrowave",
      },
      {
        property: "og:description",
        content: "Macrowave's privacy policy and data handling practices.",
      },
      {
        name: "description",
        content: "Macrowave's privacy policy and data handling practices.",
      },
    ],
  }),
});

function PrivacyPolicy() {
  return (
    <div className="mx-auto max-w-4xl py-12 px-6">
      <div className="text-center mb-12">
        <h1 className="font-serif-600 text-4xl text-black mb-4">
          Privacy Policy
        </h1>
        <p className="font-serif text-gray-600">
          Last updated: August 2025
        </p>
      </div>

      <div className="prose prose-lg max-w-none">
        <div className="font-serif text-lg leading-relaxed space-y-6 text-gray-800">
          <p>
            Macrowave and its developers take your privacy very seriously. Macrowave does not sell or rent your data.
          </p>

          <p>
            As a P2P internet radio platform, Macrowave requires an internet connection to function and transmits audio data for streaming purposes. However, Macrowave does not collect or transmit personal data externally beyond what is necessary for the core audio streaming functionality.
          </p>

          <div className="mt-12">
            <h2 className="font-serif-600 text-2xl text-black mb-4">
              Third-Party Services
            </h2>
            <p className="mb-6">
              While Macrowave is designed to be privacy-first, we use a minimal set of third-party services to provide core functionality. These services are carefully selected for their necessity and privacy practices.
            </p>

            <div className="mb-8">
              <h3 className="font-serif-600 text-xl text-black mb-3">
                RevenueCat (macOS & iOS App Only)
              </h3>
              <p className="mb-4">
                We use RevenueCat to manage subscription purchases in our iOS app. RevenueCat processes:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Anonymous app user IDs (not linked to your identity)</li>
                <li>Purchase and subscription events</li>
                <li>Basic device information (device type, OS version) for purchase validation</li>
              </ul>
              <p className="mb-4">
                RevenueCat does not have access to your audio streams, broadcast content, or personal information. This service is only active if you choose to purchase a subscription.
              </p>
              <p>
                For more information, see{" "}
                <a
                  href="https://www.revenuecat.com/privacy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-black hover:text-gray-600 transition-colors duration-200 underline"
                >
                  RevenueCat's Privacy Policy
                </a>.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="font-serif-600 text-xl text-black mb-3">
                LiveKit
              </h3>
              <p className="mb-4">
                We use LiveKit's infrastructure to facilitate the peer-to-peer WebRTC connections between broadcasters and listeners. LiveKit processes:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Temporary connection metadata (needed to establish P2P connections)</li>
                <li>Network routing information</li>
                <li>Connection quality metrics</li>
              </ul>
              <p className="mb-4">
                LiveKit does not store or have access to the actual audio content being streamed—audio flows directly between peers once the connection is established. Connection metadata is ephemeral and not retained after sessions end.
              </p>
              <p>
                For more information, see{" "}
                <a
                  href="https://livekit.io/legal/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-black hover:text-gray-600 transition-colors duration-200 underline"
                >
                  LiveKit's Privacy Policy
                </a>.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="font-serif-600 text-xl text-black mb-3">
                Meta Pixel (Website Only)
              </h3>
              <p className="mb-4">
                We use the Meta (Facebook) pixel on our website to understand the effectiveness of our marketing efforts. The Meta pixel may collect:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>IP address</li>
                <li>Browser information</li>
                <li>Page views and interactions</li>
                <li>Referral URLs</li>
                <li>Device identifiers</li>
                <li>Facebook user ID (if you're logged into Facebook)</li>
              </ul>
              <p className="mb-4">
                This data helps us understand how visitors find and use our website and may be used for retargeting advertisements on Meta platforms. The pixel is only present on our website, not in the macOS or iOS apps.
              </p>
              <p className="mb-4">
                You can opt out of Meta's tracking by:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Using the{" "}
                  <a
                    href="https://www.facebook.com/off_facebook_activity"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-black hover:text-gray-600 transition-colors duration-200 underline"
                  >
                    Facebook Off-Facebook Activity tool
                  </a>
                </li>
                <li>Installing a browser extension that blocks tracking pixels</li>
                <li>Using your browser's privacy or incognito mode</li>
              </ul>
              <p>
                For more information, see{" "}
                <a
                  href="https://www.facebook.com/privacy/policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-black hover:text-gray-600 transition-colors duration-200 underline"
                >
                  Meta's Privacy Policy
                </a>.
              </p>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <h2 className="font-serif-600 text-2xl text-black mb-4">
              Contact Us
            </h2>
            <p>
              If you have questions or concerns about any of the above please contact{" "}
              <a
                href="mailto:xoxo@lucas.love"
                className="text-black hover:text-gray-600 transition-colors duration-200 underline"
              >
                xoxo@lucas.love
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
